import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IThunkAPI } from '@/store/types';
import type {
  IFraudReviewGrpState,
  IFraudGrpOption,
  IFraudUserDetails,
  IDeleteFraudUser,
  IFraudGroupData,
  ISessionLog,
} from '../types/types';
import type { RootState } from '@/store';
import { adminFraudReviewGrpApi } from '@/api/admin/fraudGrps';

const initialState: IFraudReviewGrpState = {
  selectedUserId: 0,
  selectedFraudCheckGroupId: 0,
  fraudGrpList: [],
  fraudGroupData: {} as IFraudGroupData,
  fraudUserList: {
    users: [],
    ipAddress: [],
    pcSignature: [],
  },
  fraudUsersLoading: false,
  fraudGroupsLoading: false,
};

export const getFraudGroupsList = createAsyncThunk<IFraudGrpOption[], undefined, IThunkAPI>(
  'get/fraudReviewGrp/list',
  async (_, thunkAPI) => {
    try {
      const response = await adminFraudReviewGrpApi.getFraudReviewGrpsList();
      return response?.data || [];
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const updateFraudGroup = createAsyncThunk<boolean, IFraudGrpOption, IThunkAPI>(
  'update/fraudReviewGrp',
  async (formValues, thunkAPI) => {
    try {
      const response = await adminFraudReviewGrpApi.updateFraudReviewGroup(formValues);
      return response?.data || false;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const deleteFraudUser = createAsyncThunk<boolean, IDeleteFraudUser, IThunkAPI>(
  'delete/fraudUser',
  async (formValues, thunkAPI) => {
    try {
      const response = await adminFraudReviewGrpApi.deleteUser(formValues.fraudCheckGroupId, formValues.userId);
      return response?.data || false;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getFraudUsersList = createAsyncThunk<
  { data: IFraudUserDetails; fraudCheckGroupId: number },
  number,
  IThunkAPI
>('get/fraudUsers/list', async (fraudCheckGroupId, thunkAPI) => {
  try {
    const response = await adminFraudReviewGrpApi.getUsersList(fraudCheckGroupId);
    return { data: response?.data || [], fraudCheckGroupId: fraudCheckGroupId };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getFraudGroupList = createAsyncThunk<IFraudGroupData, number, IThunkAPI>(
  'get/fraudGroup/list',
  async (userId, thunkAPI) => {
    try {
      const response = await adminFraudReviewGrpApi.getFraudGroupList(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getSessionLogsList = createAsyncThunk<ISessionLog, number, IThunkAPI>(
  'get/sessionLogs/list',
  async (userId, thunkAPI) => {
    try {
      const response = await adminFraudReviewGrpApi.getSessionLogsList(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const adminFraudGrpReviewSlice = createSlice({
  name: 'fraudGrpReview',
  initialState,
  reducers: {
    resetFraudUserList(state) {
      state.fraudUserList = {
        users: [],
        ipAddress: [],
        pcSignature: [],
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getFraudGroupsList.fulfilled, (state, action) => {
        state.fraudGrpList = action.payload;
      })
      .addCase(getFraudUsersList.pending, state => {
        state.fraudUsersLoading = true;
        state.fraudUserList = {
          users: [],
          ipAddress: [],
          pcSignature: [],
        };
      })
      .addCase(getFraudUsersList.fulfilled, (state, action) => {
        state.fraudUsersLoading = false;
        state.fraudUserList = action.payload.data;
        state.selectedFraudCheckGroupId = action.payload.fraudCheckGroupId;
      });
  },
});
// actions
export const { resetFraudUserList } = adminFraudGrpReviewSlice.actions;

// selectors
export const getFraudReviewGrpsList = (state: RootState): IFraudReviewGrpState['fraudGrpList'] => {
  return state.fraudGrpReview.fraudGrpList.length ? state.fraudGrpReview.fraudGrpList : [];
};

export const getUsersList = (state: RootState): IFraudReviewGrpState['fraudUserList'] => {
  return state.fraudGrpReview.fraudUserList;
};

export const isFraudUsersLoading = (state: RootState): IFraudReviewGrpState['fraudUsersLoading'] =>
  state.fraudGrpReview.fraudUsersLoading;

export const selectedFraudCheckGroupId = (state: RootState): IFraudReviewGrpState['selectedFraudCheckGroupId'] =>
  state.fraudGrpReview.selectedFraudCheckGroupId;
export default adminFraudGrpReviewSlice.reducer;
